import React, { useEffect, useState } from "react";
import { AntdButton, Inner, Section } from "./styles.js";
import { Typo } from "../../../../../../components/layout/theme/index.js";
import { DatePicker, Input, Form, message, Row, Col } from "antd";
import { useReport } from "../../../../../../contexts/ReportContext.js";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import { get_quotations, clear_real_quotations } from "../../../../../../api/endpoints/quotations.js";
import QuotationLoader from "../../../../../../components/common/quotation-loader/QuotationLoader.js";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
dayjs.locale("es");

const QuotationPersonalData = ({ nextSubStep, nextMainStep, isMobile }) => {
	const [form] = Form.useForm();
	const { reportForm, updateReportForm } = useReport();
	const [initialValues, setInitialValues] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const { trackEvent } = useMatomo();

	useEffect(() => {
		const fetch_initial_values = () => {
			const values = {
				name: reportForm?.quotationPersonalData?.name,
				lastName: reportForm?.quotationPersonalData?.lastName,
				birthday: reportForm?.quotationPersonalData?.birthday,
				telephone: reportForm?.quotationPersonalData?.telephone,
				email: reportForm?.quotationPersonalData?.email,
			};

			setInitialValues(values);
		};
		fetch_initial_values();
	}, [reportForm]);

	// Validador personalizado para verificar edad mínima de 16 años
	const validateAge = (_, value) => {
		if (!value) {
			return Promise.reject(new Error("Por favor seleccione su fecha de nacimiento"));
		}
		
		const today = dayjs();
		const birthDate = dayjs(value);
		const age = today.diff(birthDate, "year");
		
		if (age < 16) {
			return Promise.reject(new Error("Debe ser mayor de 16 años"));
		}
		
		if (age > 120) {
			return Promise.reject(new Error("Por favor ingrese una fecha válida"));
		}
		
		return Promise.resolve();
	};

	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		}
	}, [initialValues, form]);

	const handleNextStep = async (values) => {
		const updatedValues = {
			name: values?.name,
			lastName: values?.lastName,
			birthday: values?.birthday,
			telephone: values?.telephone,
			email: values?.email,
		};

		// Validar que tenemos todos los datos necesarios
		if (!reportForm.carData.brandName || !reportForm.carData.modelName || !reportForm.carData.codia) {
			message.error("Faltan datos del vehículo. Por favor, complete el paso anterior.");
			return;
		}

		if (!reportForm.carLocation.province || !reportForm.carLocation.postalCode) {
			message.error("Faltan datos de ubicación. Por favor, complete el paso anterior.");
			return;
		}

		// Mostrar loader
		setIsLoading(true);

		// Enviar datos a n8n webhook
		try {
			const webhookUrl = process.env.REACT_APP_WEBHOOK_QUOTE;
			
			// Preparar payload completo con todos los datos del proceso de cotización
			const webhookPayload = {
				quotationPersonalData: updatedValues,
				carData: reportForm.carData || null,
				carLocation: reportForm.carLocation || null,
				carSpecialFeatures: reportForm.carSpecialFeatures || null,
				titularActual: reportForm.titularActual || null,
				automotor: reportForm.automotor || null,
			};
			
			console.log("Enviando datos a n8n webhook:", webhookUrl);
			console.log("Datos a enviar:", webhookPayload);

			const response = await fetch(webhookUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(webhookPayload),
			});

			if (response.ok) {
				const responseData = await response.json();
				console.log("Respuesta exitosa del webhook n8n:", responseData);
			} else {
				console.error("Error en la respuesta del webhook n8n:", response.status, response.statusText);
			}
		} catch (error) {
			console.error("Error al enviar datos al webhook n8n:", error);
		}

		try {
			// Preparar datos completos para el servicio
			const completeUserData = {
				carData: reportForm.carData,
				carLocation: reportForm.carLocation,
				carSpecialFeatures: reportForm.carSpecialFeatures,
				quotationPersonalData: updatedValues
			};

			console.log("Datos completos para cotización:", completeUserData);

			// Limpiar cache de cotizaciones anteriores para forzar nueva consulta
			clear_real_quotations();

			// Llamar al servicio de cotizaciones
			const quotations = await get_quotations(completeUserData);

			console.log("Cotizaciones obtenidas:", quotations);

			// Actualizar el contexto con los datos personales y las cotizaciones
			updateReportForm({ 
				...updatedValues, 
				quotations: quotations 
			});

			// Mostrar mensaje de éxito
			message.success("¡Cotizaciones obtenidas exitosamente!");

			// Continuar al siguiente paso
			nextSubStep(1);

		} catch (error) {
			console.error("Error al obtener cotizaciones:", error);
			
			// Mostrar mensaje de error pero continuar con datos dummy
			message.warning("Hubo un problema al obtener las cotizaciones en tiempo real. Mostramos cotizaciones de ejemplo.");
			
			// Actualizar solo los datos personales
			updateReportForm(updatedValues);
			
			// Continuar al siguiente paso (usará datos dummy)
			nextSubStep(1);
			
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Form
			form={form}
			name="quotation-personal-data-form"
			initialValues={initialValues}
			onFinish={handleNextStep}
		>
			<Inner>
				<Typo level={isMobile ? 3 : 2} style={{ textAlign: "center" }}>
					¿Nos dejás tus datos para armarte una cotización?
				</Typo>
				
				<div style={{ width: "100%", maxWidth: "800px", padding: "0 20px" }}>
					<Row gutter={[16, 16]}>
						<Col xs={24} sm={12}>
							<Form.Item
								name="name"
								rules={[
									{ required: true, message: "Por favor ingrese su nombre" },
									{ min: 3, message: "Debe tener al menos 3 letras" },
									{ pattern: /^[a-zA-ZÀ-ÿ\s]+$/, message: "Solo letras y espacios" },
								]}
							>
								<Input placeholder="Nombre" size="large" />
							</Form.Item>
						</Col>

						<Col xs={24} sm={12}>
							<Form.Item
								name="lastName"
								rules={[
									{ required: true, message: "Por favor ingrese su apellido" },
									{ min: 3, message: "Debe tener al menos 3 letras" },
									{ pattern: /^[a-zA-ZÀ-ÿ\s]+$/, message: "Solo letras y espacios" },
								]}
							>
								<Input placeholder="Apellido" size="large" />
							</Form.Item>
						</Col>

						<Col xs={24} sm={12}>
							<Form.Item
								name="birthday"
								rules={[{ validator: validateAge }]}
							>
								<DatePicker
									locale={locale}
									format="DD/MM/YYYY"
									placeholder="Fecha de nacimiento"
									size="large"
									style={{ width: "100%" }}
									disabledDate={(current) => {
										// Deshabilitar fechas futuras
										return current && current > dayjs().endOf("day");
									}}
								/>
							</Form.Item>
						</Col>

						<Col xs={24} sm={12}>
							<Form.Item
								name="telephone"
								rules={[
									{ required: true, message: "Por favor ingrese su teléfono" },
									{
										pattern: /^[0-9]{7,15}$/,
										message: "Debe contener solo números (7 a 15 dígitos)",
									},
								]}
							>
								<Input placeholder="Teléfono" size="large" />
							</Form.Item>
						</Col>

						<Col xs={24}>
							<Form.Item
								name="email"
								rules={[
									{ required: true, message: "Por favor ingrese su email" },
									{ type: "email", message: "El formato del email no es válido" },
								]}
							>
								<Input placeholder="Email" size="large" />
							</Form.Item>
						</Col>
					</Row>
				</div>
				
				<Section style={{ maxWidth: "250px", maxHeight: "100px" }}>
					<AntdButton
						key="PrevStep2CarParsonalData"
						onClick={() => {
							// Limpiar cache de cotizaciones al volver atrás
							clear_real_quotations();
							nextMainStep(0);
						}}
						style={{ padding: "20px 20px" }}
					>
						<Typo level={5}>Anterior</Typo>
					</AntdButton>
					<AntdButton
						key="NextStep2CarPersonalData"
						type="primary"
						htmlType="submit"
						style={{ padding: "20px 20px" }}
					>
						<Typo level={5} type="white">
							Siguiente
						</Typo>
					</AntdButton>
				</Section>
			</Inner>
			
			{/* Loader educativo */}
			<QuotationLoader isVisible={isLoading} />
		</Form>
	);
};

export default QuotationPersonalData;
